/**
 * Centralized accessor map for all Effector entity stores and their APIs.
 *
 * Provides a single registry so the field-binding and API-contract systems
 * can read/write stores by name without importing each entity individually.
 */
import { $account, accountApi } from "../../../entities/account";
import { $quote, quoteApi } from "../../../entities/quote";
import { $appointment, appointmentApi } from "../../../entities/appointment";
import {
  $bookingProcess,
  bookingProcessApi,
} from "../../../entities/booking-process";
import {
  $serviceLocation,
  serviceLocationApi,
} from "../../../entities/locations";

export type StoreName =
  | "account"
  | "quote"
  | "appointment"
  | "bookingProcess"
  | "serviceLocation";

export const storeAccessors: Record<StoreName, { $store: any; api: any }> = {
  account: { $store: $account, api: accountApi },
  quote: { $store: $quote, api: quoteApi },
  appointment: { $store: $appointment, api: appointmentApi },
  bookingProcess: { $store: $bookingProcess, api: bookingProcessApi },
  serviceLocation: { $store: $serviceLocation, api: serviceLocationApi },
};

/**
 * Reads a dot-notated path from a store's current state.
 *
 * For account/quote/appointment the actual data lives one level
 * deeper (e.g. `$account → { account: {...}, loading }`) so we
 * dereference that wrapper automatically.
 */
export const readStorePath = (storeName: StoreName, path: string): unknown => {
  const raw = storeAccessors[storeName].$store.getState();

  // Unwrap the entity wrapper (account → .account, quote → .quote, etc.)
  const innerKey: Record<StoreName, string | null> = {
    account: "account",
    quote: "quote",
    appointment: "appointment",
    bookingProcess: null, // flat – no wrapper
    serviceLocation: "location",
  };

  let root = innerKey[storeName] ? raw?.[innerKey[storeName]!] : raw;

  if (!root) return undefined;

  const segments = path.split(".");
  for (const seg of segments) {
    if (root == null) return undefined;
    root = root[seg];
  }
  return root;
};
